import { useCallback, useEffect, useState } from "react";
import {
  DEFAULT_ESTIMATE_SETTINGS,
  type EstimateSettings,
  type OverrideMap,
  type UnitOverride,
} from "@/lib/pipe-estimating";

const SETTINGS_KEY = "tt-pipe-estimate-settings";
const OVERRIDE_KEY = "tt-pipe-estimate-overrides";

function read<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? { ...fallback, ...parsed } : fallback;
  } catch {
    return fallback;
  }
}

/**
 * Estimating rates and per-line unit overrides.
 * Stored on the device so a crew's own numbers survive offline reloads.
 */
export function useEstimateSettings() {
  const [settings, setSettings] = useState<EstimateSettings>(DEFAULT_ESTIMATE_SETTINGS);
  const [overrides, setOverrides] = useState<OverrideMap>({});
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setSettings(read(SETTINGS_KEY, DEFAULT_ESTIMATE_SETTINGS));
    setOverrides(read<OverrideMap>(OVERRIDE_KEY, {}));
    setLoaded(true);
  }, []);

  useEffect(() => {
    if (!loaded) return;
    try {
      localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
    } catch {
      /* storage full — ignore */
    }
  }, [settings, loaded]);

  useEffect(() => {
    if (!loaded) return;
    try {
      localStorage.setItem(OVERRIDE_KEY, JSON.stringify(overrides));
    } catch {
      /* storage full — ignore */
    }
  }, [overrides, loaded]);

  const setSetting = useCallback(<K extends keyof EstimateSettings>(k: K, v: EstimateSettings[K]) => {
    setSettings((s) => ({ ...s, [k]: v }));
  }, []);

  const resetSettings = useCallback(() => setSettings(DEFAULT_ESTIMATE_SETTINGS), []);

  const setOverride = useCallback((key: string, patch: UnitOverride) => {
    setOverrides((o) => {
      const next = { ...o, [key]: { ...o[key], ...patch } };
      const entry = next[key];
      if (entry.unitHours == null && entry.unitCost == null) delete next[key];
      return next;
    });
  }, []);

  const clearOverride = useCallback((key: string) => {
    setOverrides((o) => {
      const next = { ...o };
      delete next[key];
      return next;
    });
  }, []);

  const clearAllOverrides = useCallback(() => setOverrides({}), []);

  return {
    settings,
    setSetting,
    resetSettings,
    overrides,
    setOverride,
    clearOverride,
    clearAllOverrides,
    overrideCount: Object.keys(overrides).length,
  };
}
